/**
 * Protocol scheduling and adherence.
 *
 * Works out which days a protocol calls for a dose, and compares that against
 * what was logged. Reports counts and dates only -- it does not characterise
 * adherence as good or bad.
 */
import { addDays, daysBetween, weekdayOf, type Day } from '../lib/time';
import type { Protocol } from '../data/schema';

/** Every day in [from, to] the protocol calls for, inclusive. */
export function scheduledDays(protocol: Protocol, from: Day, to: Day): Day[] {
  if (daysBetween(from, to) < 0) return [];
  const start = protocol.startDay > from ? protocol.startDay : from;
  const end = protocol.endDay && protocol.endDay < to ? protocol.endDay : to;
  if (daysBetween(start, end) < 0) return [];

  const out: Day[] = [];
  switch (protocol.kind) {
    case 'daily': {
      for (let d = start; daysBetween(d, end) >= 0; d = addDays(d, 1)) out.push(d);
      break;
    }
    case 'weekly': {
      const want = new Set(protocol.weekdays ?? []);
      if (!want.size) return [];
      for (let d = start; daysBetween(d, end) >= 0; d = addDays(d, 1)) {
        if (want.has(weekdayOf(d))) out.push(d);
      }
      break;
    }
    case 'interval': {
      const step = protocol.intervalDays ?? 0;
      if (step <= 0) return [];
      // Anchor the cycle on startDay so the phase is stable regardless of the
      // window being queried.
      const offset = daysBetween(protocol.startDay, start);
      const firstHit = offset % step === 0 ? start : addDays(start, step - (offset % step));
      for (let d = firstHit; daysBetween(d, end) >= 0; d = addDays(d, step)) out.push(d);
      break;
    }
    case 'asneeded':
      return [];
  }
  return out;
}

/** The next scheduled day on or after `from`, or null if the protocol has ended. */
export function nextDueDay(protocol: Protocol, from: Day): Day | null {
  if (protocol.kind === 'asneeded') return null;
  // A year is a generous horizon; any protocol with no hit in 366 days is inert.
  const horizon = addDays(from, 366);
  return scheduledDays(protocol, from, horizon)[0] ?? null;
}

export interface Adherence {
  scheduled: number;
  taken: number;
  skipped: number;
  /** Scheduled days with neither a dose nor an explicit skip. */
  missed: Day[];
  /** Doses logged on days the protocol did not call for. */
  extra: Day[];
  /** taken / scheduled, 0..1. Null when nothing was scheduled. */
  rate: number | null;
}

/**
 * Compare a protocol's schedule against the log.
 *
 * `takenDays` and `skippedDays` are the days a dose was recorded or explicitly
 * marked skipped. A skip counts as adherence to the log, not to the protocol:
 * it is reported separately and excluded from `missed`, but it does not count
 * toward `taken`.
 */
export function adherence(
  protocol: Protocol, from: Day, to: Day, takenDays: Day[], skippedDays: Day[] = [],
): Adherence {
  const scheduled = scheduledDays(protocol, from, to);
  const scheduledSet = new Set(scheduled);
  const takenSet = new Set(takenDays);
  const skippedSet = new Set(skippedDays);

  const missed = scheduled.filter((d) => !takenSet.has(d) && !skippedSet.has(d));
  const extra = [...takenSet].filter((d) => !scheduledSet.has(d) && d >= from && d <= to).sort();
  const taken = scheduled.filter((d) => takenSet.has(d)).length;

  return {
    scheduled: scheduled.length,
    taken,
    skipped: scheduled.filter((d) => skippedSet.has(d)).length,
    missed,
    extra,
    rate: scheduled.length ? taken / scheduled.length : null,
  };
}
